import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import EditIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import {
  BorrowerDetail,
  BorrowerLoan,
  LoanSnapshot,
} from "../../../models/dto/cat15/cat15.types";
import { getLoanSnapshots } from "../../../service/cat15.service";
import DqsBadge from "../../../components/cat15/DqsBadge";
import LoanStatusChip from "../../../components/cat15/LoanStatusChip";
import { assetClassLabelKey } from "../../../components/cat15/labels";
import {
  FeCell,
  FeHeadCell,
  FeRow,
  FeSubHeadCell,
  FeTable,
  FeTableScroll,
  FeTableWrap,
} from "../../../components/cat15/FeTable";
import FePagination from "../../../components/cat15/FePagination";
import { formatNumber } from "../format";

interface Props {
  borrower: BorrowerDetail;
  onAddLoan: () => void;
  onAddSnapshot: (loan: BorrowerLoan) => void;
  onEditSnapshot: (loan: BorrowerLoan, snapshot: LoanSnapshot) => void;
  /** bumped by the parent after a snapshot edit so this loan re-fetches in place */
  editedLoan?: { id: string; seq: number };
}

/** Linked Loans tab — Figma node 6086:44746 (main table + expandable snapshots). */
const DetailLoans = ({
  borrower,
  onAddLoan,
  onAddSnapshot,
  onEditSnapshot,
  editedLoan,
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Per-year snapshots are lazy-loaded on expand — the composite detail carries
  // only outstanding/asOfDate, not the engine tco2e/dqs shown in this panel.
  const [snapshotsByLoan, setSnapshotsByLoan] = useState<
    Record<string, LoanSnapshot[]>
  >({});
  const [loadingLoanId, setLoadingLoanId] = useState<string | null>(null);

  const fetchSnapshots = useCallback((loanId: string) => {
    setLoadingLoanId(loanId);
    getLoanSnapshots(loanId)
      .then((rows) =>
        setSnapshotsByLoan((prev) => ({ ...prev, [loanId]: rows })),
      )
      .catch(() => {}) // already surfaced by the service layer
      .finally(() =>
        setLoadingLoanId((cur) => (cur === loanId ? null : cur)),
      );
  }, []);

  const toggleExpand = (loan: BorrowerLoan) => {
    if (expanded === loan.id) {
      setExpanded(null);
      return;
    }
    setExpanded(loan.id);
    if (!snapshotsByLoan[loan.id]) fetchSnapshots(loan.id);
  };

  // After an edit the parent bumps editedLoan.seq → re-pull that loan's rows
  // without collapsing/remounting the table.
  useEffect(() => {
    if (editedLoan) fetchSnapshots(editedLoan.id);
  }, [editedLoan, fetchSnapshots]);

  const reportingYear = borrower.emissions[0]?.year ?? new Date().getFullYear();

  if (borrower.loans.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap="12px"
        py="40px"
      >
        <Typography variant="body1" color="var(--gray1)">
          {t("No-Loans")}
        </Typography>
        <Button variant="contained" color="green" onClick={onAddLoan}>
          + {t("Add-Loan")}
        </Button>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {/* Section header — "All loans…" + Attach New Loan */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontSize="18px" fontWeight={600} color="var(--dark)">
          {t("Loans-Header")}
        </Typography>
        <Button
          variant="contained"
          color="green"
          onClick={onAddLoan}
          startIcon={<AddIcon />}
          sx={{ padding: "8px 12px" }}
        >
          {t("Attach-New-Loan")}
        </Button>
      </Box>

      <FeTableWrap>
        <FeTableScroll>
        <FeTable>
          <TableHead>
            <TableRow>
              <FeHeadCell sx={{ width: 48 }} />
              <FeHeadCell>{t("Col-Loan-Ref")}</FeHeadCell>
              <FeHeadCell>{t("Col-Asset-Class")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Principal")}</FeHeadCell>
              <FeHeadCell align="right">{t("Col-Outstanding")}</FeHeadCell>
              <FeHeadCell align="center">{t("Col-Status")}</FeHeadCell>
              <FeHeadCell align="right">
                {t("Col-tCO2e", { year: reportingYear })}
              </FeHeadCell>
              <FeHeadCell align="center">{t("DQS")}</FeHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {borrower.loans
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((loan) => {
              const open = expanded === loan.id;
              const rows = snapshotsByLoan[loan.id] ?? loan.snapshots;
              const loading = loadingLoanId === loan.id && !snapshotsByLoan[loan.id];
              return (
                <Fragment key={loan.id}>
                  <FeRow>
                    <FeCell>
                      <IconButton size="small" onClick={() => toggleExpand(loan)}>
                        {open ? (
                          <KeyboardArrowDownIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowRightIcon fontSize="small" />
                        )}
                      </IconButton>
                    </FeCell>
                    <FeCell>{loan.loanRef}</FeCell>
                    <FeCell>{t(assetClassLabelKey(loan.assetClass))}</FeCell>
                    <FeCell align="right">{formatNumber(loan.principal)}</FeCell>
                    <FeCell align="right">
                      {formatNumber(loan.outstanding)}
                    </FeCell>
                    <FeCell align="center">
                      <LoanStatusChip status={loan.status} />
                    </FeCell>
                    <FeCell align="right">{formatNumber(loan.tco2e)}</FeCell>
                    <FeCell align="center">
                      <DqsBadge level={loan.dqs} />
                    </FeCell>
                  </FeRow>
                  <TableRow>
                    <FeCell colSpan={8} sx={{ p: 0, height: "auto" }}>
                      <Collapse in={open} unmountOnExit>
                        <Box
                          display="flex"
                          flexDirection="column"
                          gap="16px"
                          p="20px"
                          bgcolor="var(--gray7)"
                        >
                          <FeTableWrap>
                            <FeTable>
                              <TableHead>
                                <TableRow>
                                  <FeSubHeadCell>{t("Col-Year")}</FeSubHeadCell>
                                  <FeSubHeadCell align="right">
                                    {t("Col-Outstanding")}
                                  </FeSubHeadCell>
                                  <FeSubHeadCell align="right">
                                    {t("Col-Emission")}
                                  </FeSubHeadCell>
                                  <FeSubHeadCell align="center">
                                    {t("DQS")}
                                  </FeSubHeadCell>
                                  <FeSubHeadCell align="center" sx={{ width: 80 }}>
                                    {t("Col-Action")}
                                  </FeSubHeadCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {loading ? (
                                  <TableRow>
                                    <FeCell
                                      colSpan={5}
                                      align="center"
                                      sx={{ color: "var(--gray1)" }}
                                    >
                                      …
                                    </FeCell>
                                  </TableRow>
                                ) : (
                                  rows.map((sn) => (
                                    <FeRow key={sn.year}>
                                      <FeCell>{sn.year}</FeCell>
                                      <FeCell align="right">
                                        {formatNumber(sn.outstanding)}
                                      </FeCell>
                                      <FeCell align="right">
                                        {formatNumber(sn.tco2e)}
                                      </FeCell>
                                      <FeCell align="center">
                                        <DqsBadge level={sn.dqs} />
                                      </FeCell>
                                      <FeCell align="center">
                                        <IconButton
                                          size="small"
                                          onClick={() => onEditSnapshot(loan, sn)}
                                          aria-label={t("Edit")}
                                          sx={{ color: "var(--green)", p: "2px" }}
                                        >
                                          <EditIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                      </FeCell>
                                    </FeRow>
                                  ))
                                )}
                              </TableBody>
                            </FeTable>
                          </FeTableWrap>
                          <Button
                            variant="text"
                            color="green"
                            startIcon={<AddIcon />}
                            onClick={() => onAddSnapshot(loan)}
                            sx={{ alignSelf: "flex-start", p: 0, minWidth: 0 }}
                          >
                            {t("Add-Snapshot")}
                          </Button>
                        </Box>
                      </Collapse>
                    </FeCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </FeTable>
        </FeTableScroll>
        <FePagination
          count={borrower.loans.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      </FeTableWrap>
    </Box>
  );
};

export default DetailLoans;
